//@ts-check
import _ from 'lodash';
import Joi from 'joi-browser';
import { STATUS_CODE } from '../../utilities/constants';
import { LwUser, LwUsers, EditName } from './Base';
import LwGroupApi from '../LwGroupApi';
import LwTalkroomApi from '../LwTalkroomApi';
import { HttpError } from './Error';

/*
LINE WOKRSモーダル内のトークルーム関係のみで取り扱うクラスや関数。
「_」付きの変数や関数を直接取得・変更してはならない。
(プライベート宣言はESLint非対応のため未使用)
*/

class AppendableUser extends LwUser {
  /**
   * LINE WROKSユーザーの情報を格納するクラス
   * @param {any} parameter
   * 詳細はLwUserクラスを参照
   **/
}

class AppendableUsers extends LwUsers {
  /**
   * 追加可能なユーザーの情報を格納するクラス
   * @param {AppendableUser[]} appendableUserList - 追加可能なユーザーのリスト
   **/
  constructor(appendableUserList) {
    super(appendableUserList);
  }

  /**
   * LINE WORKS グループ詳細取得APIから本クラスを作る。
   * @param {number} fieldId 現場ID
   * @param {number} lwGroupId グループID
   * @param {import("./SearchCondition").SearchCondition} searchCondition 検索条件
   * @return {Promise<AppendableUsers>} 取得出来たユーザー
   */
  static async createByApiData(fieldId, lwGroupId, searchCondition) {
    if (!lwGroupId) {
      return new AppendableUsers([]);
    }
    const { data, status } = await LwGroupApi.detail(
      fieldId,
      lwGroupId,
      searchCondition.getAPIParamertes()
    );
    if (status === STATUS_CODE.badRequest) {
      // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
      // @ts-ignore
      throw new HttpError(_.get(data, 'message', '') || '');
    }
    const originAppendableUsers =
      _.get(data, 'entities.lw_group.members', []) || [];
    const appendableUsers = _.map(originAppendableUsers, function(userData) {
      return new AppendableUser(userData);
    });
    return new AppendableUsers(appendableUsers);
  }
  /**
   * @param {LwUser[]} otherLwUserList 追加しようとしている他のユーザークラスのリスト
   * @return {AppendableUsers} ユーザ追加後のLINE WROKSユーザークラス
   **/
  addOtherUsers(otherLwUserList) {
    return super._addOtherUsers(
      otherLwUserList,
      AppendableUser,
      AppendableUsers
    );
  }

  /**
   * @param {number[]} lwUserIds 削除しようとしているユーザーのID配列
   * @return {AppendableUsers} ユーザ削除後のLINE WROKSユーザークラス
   **/
  removeUsers(lwUserIds) {
    return super._removeUsers(lwUserIds, AppendableUsers);
  }
}

class AssignUser extends LwUser {
  /**
   * 追加可能なユーザーの情報を格納するクラス
   * @param {any} parameter
   * 詳細はLwUserクラスを参照
   **/
}

class AssignUsers extends LwUsers {
  /**
   * 追加可能なユーザーの情報を格納するクラス
   * @param {AssignUser[]} assignUserList - 追加可能なユーザーのリスト
   **/
  constructor(assignUserList) {
    super(assignUserList);
  }

  /**
   * @param {LwUser[]} otherLwUserList 追加しようとしている他のユーザークラスのリスト
   * @return {AssignUsers} ユーザ追加後のLINE WROKSユーザークラス
   **/
  addOtherUsers(otherLwUserList) {
    return super._addOtherUsers(otherLwUserList, AssignUser, AssignUsers);
  }

  /**
   * @param {number[]} lwUserIds 削除しようとしているユーザーのID配列
   * @return {AssignUsers} ユーザ削除後のLINE WROKSユーザークラス
   **/
  removeUsers(lwUserIds) {
    return super._removeUsers(lwUserIds, AssignUsers);
  }
}

class ShowUser extends LwUser {
  /**
   * 閲覧用ユーザーの情報を格納するクラス
   * @param {any} parameter
   * 詳細はLwUserクラスを参照
   **/
}

class ShowUsers extends LwUsers {
  /**
   * 閲覧用ユーザーの情報を格納するクラス
   * @param {ShowUser[]} showUserList - 閲覧用ユーザーのリスト
   **/
  constructor(showUserList) {
    super(showUserList);
  }

  /**
   * LINE WORKS トークルーム詳細取得APIから本クラスを作る。
   * @param {number} fieldId 現場ID
   * @param {number} lwTalkroomId トークルームID
   * @param {import("./SearchCondition").SearchCondition} searchCondition 検索条件
   * @return {Promise<ShowUsers>} 取得出来たユーザー
   */
  static async createByApiData(fieldId, lwTalkroomId, searchCondition) {
    if (!lwTalkroomId) {
      return new ShowUsers([]);
    }
    const { data, status } = await LwTalkroomApi.detail(
      fieldId,
      lwTalkroomId,
      searchCondition.getAPIParamertes()
    );
    if (status === STATUS_CODE.badRequest) {
      // 型情報がおかしいが、正常にエラーを呼び出せるので無視する
      // @ts-ignore
      throw new HttpError(_.get(data, 'message', '') || '');
    }
    const originShowUsers =
      _.get(data, 'entities.lw_talkroom.members', []) || [];
    const showUsers = _.map(originShowUsers, function(userData) {
      return new ShowUser(userData);
    });
    return new ShowUsers(showUsers);
  }
}

const talkroomNameSchema = Joi.object().keys({
  name: Joi.string()
    .max(1000)
    .required(),
});

class TalkroomName extends EditName {
  _schema = Joi.object().keys({
    name: Joi.string()
      .max(1000)
      .required(),
  });

  /**
   * トークルーム名を格納するクラス
   * @param {string} talkroomName - トークルーム名
   **/
  constructor(talkroomName) {
    super(talkroomName, talkroomNameSchema);
  }

  /**
   * LINE WORKS トークルーム詳細取得APIから本クラスを作る。
   * @param {number} fieldId 現場ID
   * @param {number} talkRoomId トークルームID
   * @return {Promise<TalkroomName>} 取得出来たトークルーム名
   */
  static async createByApiData(fieldId, talkRoomId) {
    if (!talkRoomId) {
      return new TalkroomName('');
    }
    const { data } = await LwTalkroomApi.detail(fieldId, talkRoomId);
    /** @type {string} **/
    const talkroomName =
      _.get(data, 'data.entities.lw_talkroom.name', '') || '';
    return new TalkroomName(talkroomName);
  }

  /**
   * @param {string} talkroomName - トークルーム名
   * @return {TalkroomName}
   **/
  setValue(talkroomName) {
    return super._setValue(talkroomName, TalkroomName, talkroomNameSchema);
  }
}

export {
  AppendableUser,
  AppendableUsers,
  AssignUser,
  AssignUsers,
  ShowUser,
  ShowUsers,
  TalkroomName,
};



// WEBPACK FOOTER //
// ./src/services/lwUser/Talkroom.js